import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { CircleAlert, FileSearch, Search, X } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import type { KbDocument } from "@/api/generated/KbDocument"
import type { KbSearchHit } from "@/api/generated/KbSearchHit"
import { useKbFolders, useKbSearch, useKbStatus } from "@/api/queries/knowledge"
import { PageContainer, PageHeader } from "@/components/app/page"
import { EmptyState, ErrorState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { DocIcon } from "@/features/knowledge/doc-bits"
import { DocumentSheet } from "@/features/knowledge/document-sheet"
import { FolderSelect } from "@/features/knowledge/folder-select"
import { useIndexJobWatcher } from "@/features/knowledge/index-panel"
import {
  fileName,
  groupHits,
  type HitGroup,
  highlightSegments,
  parentFolder,
  queryTokens,
} from "@/features/knowledge/kb-logic"
import { MoveDialog } from "@/features/knowledge/path-dialogs"

const LIMITS = [10, 20, 50, 100] as const

interface KbSearchParams {
  q?: string
  folder?: string
  limit?: number
  doc?: number
}

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : undefined)

export const Route = createFileRoute("/_app/knowledge/search")({
  staticData: { titleKey: "nav.items.knowledgeSearch" },
  validateSearch: (s: Record<string, unknown>): KbSearchParams => ({
    q: str(s.q),
    folder: str(s.folder),
    limit: LIMITS.includes(Number(s.limit) as never) ? Number(s.limit) : undefined,
    doc: Number(s.doc) > 0 ? Number(s.doc) : undefined,
  }),
  component: KnowledgeSearchPage,
})

function KnowledgeSearchPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setSearch = (patch: Partial<KbSearchParams>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const [draft, setDraft] = React.useState(search.q ?? "")
  React.useEffect(() => setDraft(search.q ?? ""), [search.q])
  const inputRef = React.useRef<HTMLInputElement>(null)

  const status = useKbStatus()
  useIndexJobWatcher(status.data)
  const folders = useKbFolders()
  const limit = search.limit ?? 20
  const query = search.q ?? ""
  const results = useKbSearch({ query, folder: search.folder, limit })
  const groups = React.useMemo(() => groupHits(results.data?.items ?? []), [results.data])
  const tokens = React.useMemo(() => queryTokens(query), [query])
  const [moving, setMoving] = React.useState<KbDocument | null>(null)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const q = draft.trim()
    if (q === query) void results.refetch()
    else setSearch({ q: q || undefined, doc: undefined })
  }

  const searching = results.isFetching
  const noDocs = status.data?.total_docs === 0

  return (
    <PageContainer width="default">
      <PageHeader
        title={t("knowledge.search.title")}
        description={t("knowledge.search.description")}
        icon={FileSearch}
      />

      <form onSubmit={submit} className="space-y-3" role="search">
        <InputGroup className="h-11 text-base">
          <InputGroupAddon>{searching ? <Spinner /> : <Search />}</InputGroupAddon>
          <InputGroupInput
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("knowledge.search.placeholder")}
            aria-label={t("knowledge.search.placeholder")}
            autoFocus
            maxLength={2000}
            enterKeyHint="search"
          />
          {draft && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                aria-label={t("knowledge.search.clear")}
                onClick={() => {
                  setDraft("")
                  setSearch({ q: undefined, doc: undefined })
                  inputRef.current?.focus()
                }}
              >
                <X />
              </InputGroupButton>
            </InputGroupAddon>
          )}
          <InputGroupAddon align="inline-end">
            <InputGroupButton type="submit" variant="default" className="h-7 px-3" disabled={!draft.trim()}>
              {t("knowledge.search.submit")}
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
        <div className="flex flex-wrap items-center gap-2">
          <FolderSelect
            size="sm"
            className="h-8 w-auto max-w-64"
            folders={folders.data ?? []}
            value={search.folder ?? ""}
            onChange={(f) => setSearch({ folder: f || undefined })}
            allLabel={t("knowledge.search.allFolders")}
            aria-label={t("knowledge.search.folder")}
          />
          <Select
            value={String(limit)}
            onValueChange={(v) => setSearch({ limit: Number(v) === 20 ? undefined : Number(v) })}
          >
            <SelectTrigger size="sm" className="h-8 w-auto" aria-label={t("knowledge.search.limit")}>
              <SelectValue>
                {(v: string) => t("knowledge.search.limitValue", { count: Number(v) })}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {LIMITS.map((l) => (
                <SelectItem key={l} value={String(l)}>
                  {t("knowledge.search.limitValue", { count: l })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {status.data && (
            <Tooltip>
              <TooltipTrigger render={<span className="ml-auto inline-flex" />}>
                <StatusBadge tone={status.data.search_mode === "hybrid" ? "accent" : "neutral"}>
                  {t(`knowledge.mode.${status.data.search_mode === "hybrid" ? "hybrid" : "keyword"}`)}
                </StatusBadge>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {status.data.search_mode === "hybrid"
                  ? t("knowledge.search.hybridHint")
                  : t("knowledge.search.keywordHint")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </form>

      <div className="mt-6 space-y-3" aria-live="polite" aria-busy={searching}>
        {!query ? (
          noDocs ? (
            <EmptyState
              icon={FileSearch}
              title={t("knowledge.search.noDocs.title")}
              description={t("knowledge.search.noDocs.description")}
              action={
                <Button size="sm" nativeButton={false} render={<Link to="/knowledge" />}>
                  {t("knowledge.search.noDocs.action")}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Search}
              title={t("knowledge.search.idle.title")}
              description={t("knowledge.search.idle.description")}
            />
          )
        ) : results.isError ? (
          <ErrorState error={results.error} onRetry={() => void results.refetch()} />
        ) : results.isPending ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)
        ) : (
          <>
            {results.data.warning && (
              <Alert>
                <CircleAlert />
                <AlertDescription>{results.data.warning}</AlertDescription>
              </Alert>
            )}
            {groups.length === 0 ? (
              <EmptyState
                icon={FileSearch}
                title={t("knowledge.search.empty.title")}
                description={t("knowledge.search.empty.description", { query })}
              />
            ) : (
              <>
                <p className="text-muted-foreground text-xs">
                  {t("knowledge.search.resultCount", { count: results.data.items.length })}{" "}
                  {t("knowledge.search.resultDocs", { count: groups.length })}
                </p>
                <ol className="space-y-3">
                  {groups.map((g) => (
                    <HitCard
                      key={g.document_id}
                      group={g}
                      tokens={tokens}
                      active={search.doc === g.document_id}
                      onOpen={() => setSearch({ doc: g.document_id })}
                    />
                  ))}
                </ol>
              </>
            )}
          </>
        )}
      </div>

      <DocumentSheet
        id={search.doc}
        onClose={() => setSearch({ doc: undefined })}
        onOpen={(id) => setSearch({ doc: id })}
        onMove={setMoving}
        embeddingsConfigured={!!status.data?.embedding.configured}
        highlight={tokens}
        showInFolder
      />
      <MoveDialog
        target={moving ? { path: moving.path, kind: "document" } : null}
        onOpenChange={(o) => !o && setMoving(null)}
        folders={folders.data ?? []}
      />
    </PageContainer>
  )
}

function Highlighted({ text, tokens }: { text: string; tokens: string[] }) {
  return (
    <>
      {highlightSegments(text, tokens).map((s, i) =>
        s.match ? (
          <mark key={i} className="bg-warning/30 text-foreground rounded-sm px-0.5">
            {s.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{s.text}</React.Fragment>
        ),
      )}
    </>
  )
}

function HitCard({
  group,
  tokens,
  active,
  onOpen,
}: {
  group: HitGroup
  tokens: string[]
  active: boolean
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const name = fileName(group.path)
  const folder = parentFolder(group.path)
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={`bg-card hover:border-foreground/20 focus-visible:ring-ring/50 group block w-full rounded-xl border p-4 text-left transition-colors outline-none focus-visible:ring-2 ${active ? "border-primary/50 ring-primary/20 ring-2" : ""}`}
      >
        <div className="flex items-start gap-3">
          <div className="bg-muted text-muted-foreground mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg">
            <DocIcon path={group.path} />
          </div>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex items-baseline gap-2">
              <span className="text-muted-foreground shrink-0 text-xs tabular-nums">#{group.rank}</span>
              <h3 className="truncate font-medium group-hover:underline">
                <Highlighted text={group.title || name} tokens={tokens} />
              </h3>
            </div>
            <p className="text-muted-foreground truncate font-mono text-xs">
              {folder ? `${folder}/` : ""}
              <span className="text-foreground/80">{name}</span>
            </p>
          </div>
        </div>
        <div className="mt-3 space-y-3">
          {group.hits.slice(0, 3).map((h) => (
            <HitSnippet key={h.chunk_id} hit={h} tokens={tokens} />
          ))}
          {group.hits.length > 3 && (
            <p className="text-muted-foreground text-xs">
              {t("knowledge.search.moreChunks", { count: group.hits.length - 3 })}
            </p>
          )}
        </div>
      </button>
    </li>
  )
}

function HitSnippet({ hit, tokens }: { hit: KbSearchHit; tokens: string[] }) {
  const { t } = useTranslation()
  return (
    <div className="border-l-2 pl-3">
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        {hit.heading && hit.heading_level > 0 && <span className="text-xs font-medium">{hit.heading}</span>}
        {hit.vector_score != null && (
          <StatusBadge tone="accent" title={t("knowledge.search.similarityHint")}>
            {t("knowledge.search.similarity", { value: Math.round(hit.vector_score * 100) })}
          </StatusBadge>
        )}
        {hit.keyword_score != null && (
          <StatusBadge tone="info" title={t("knowledge.search.keywordMatchHint")}>
            {t("knowledge.search.keywordMatch")}
          </StatusBadge>
        )}
      </div>
      <p className="text-muted-foreground line-clamp-4 text-[0.8125rem] leading-relaxed break-words whitespace-pre-line">
        <Highlighted text={hit.excerpt} tokens={tokens} />
      </p>
    </div>
  )
}
